import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { BotaoDoRaro, EtiquetaDoPreenchido, LinhaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { SemPermissao } from '@/componentes/SemPermissao';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDataHora, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { apoioLigado } from './apoio';
import { CampoDoCodigo } from './CampoDoCodigo';
import { useCadastrarEquipamento, useCadastrosDeApoio, useProximoCodigo, useTiposDeEquipamento } from './consultas-frota';
import {
  erroDaPlaca,
  erroDoAno,
  erroDoAnoAquisicao,
  erroDoAnoModelo,
  erroDoCodigoInterno,
  erroDoRenavam,
  lerFichaComplementar,
} from './ficha';
import { COMBUSTIVEIS } from './frota';
import { descricaoSugerida, etiquetasHerdadas, medidorEfetivo, rotuloDaLeituraInicial } from './heranca-do-cadastro';
import { MensagemDeErro } from './MensagemDeErro';
import { opcoesDeTipo, textoDoCodigoNovo, tipoDaLista, tipoSugeridoDoModelo } from './tipos';
import { CaixaAbasteceEmPosto } from './CaixaAbasteceEmPosto';
import { BARRA_DO_CARTAO, CARTAO_DE_FORMULARIO, SecaoDoFormulario } from './SecaoDoFormulario';
import { opcaoDaConfiguracao } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * Cadastrar equipamento — a unidade da ferramenta 1.
 *
 * ## Duas camadas, e o que e OPCIONAL marcado (31/10/2026)
 *
 * Eram dezenove campos em cinco secoes, e o cadastro minimo pedia rolar a
 * pagina inteira. Agora, no desenho do lancamento de abastecimento (pedido do
 * dono: "as melhorias que fizemos no formulario de combustivel devem ser
 * levadas aos outros formularios"):
 *
 *  - a vista, em tres linhas da grade, o que se digita: tipo, codigo interno,
 *    configuracao, descricao, placa, ano e a leitura inicial;
 *  - o que a CONFIGURACAO responde (medidor, tanque, motor) aparece em
 *    etiqueta, e os campos de sobrescrever abrem em "Ajustar medidor e tanque";
 *  - o raro (filial, patrimonio, combustivel, numeros de serie, Renavam, anos
 *    do modelo e da aquisicao, posto parceiro) fica em "Mais dados".
 *
 * Nenhum campo saiu, e a camada com erro abre sozinha. A configuracao sugere
 * a descricao (tipo, fabricante e modelo), com a marca de pre-preenchido; a
 * empresa com uma filial so ja a tem escolhida (`heranca-do-cadastro.ts`).
 * A marca do opcional e da p.16: o obrigatorio e TIPO e descricao.
 *
 * ## O codigo e do sistema (0131, decisao 57)
 *
 * Ninguem digita o codigo: ele nasce do tipo e da sequencia ao gravar ("a
 * setima escavadeira e ESC-007"). A tela mostra a PREVIA que a API calcula,
 * renovada a cada troca de tipo, e o pedido nunca leva `codigo`. O tipo sai da
 * lista da empresa, e escolher a configuracao ja escolhe o tipo do modelo
 * quando ele esta na lista.
 *
 * ## Nulo NAO e "faltou preencher"
 *
 * Medidor e capacidade em branco querem dizer HERDA DA CONFIGURACAO, e a ajuda
 * de cada campo diz isso com todas as letras. Se a tela tratasse vazio como
 * falta de dado, quem cadastra preencheria os dois na mao em toda maquina — e a
 * configuracao, que existe justamente para nao repetir isso, viraria enfeite.
 *
 * ## Validacao ao sair do campo
 *
 * `validar` roda no `blur`, nunca a cada tecla: quem digita "19" a caminho de
 * "1998" nao errou nada (p.16). As recusas que so o banco conhece — codigo
 * repetido, numero de serie repetido — voltam ja traduzidas em mensagem que diz
 * o que fazer.
 *
 * ## Rascunho automatico
 *
 * Sao dezoito campos, e a p.16 manda formulario com mais de dez salvar rascunho:
 * "conexao em obra cai, e o preenchimento nao pode se perder". O rascunho mora
 * NESTE NAVEGADOR, por empresa, pessoa e tela (`useRascunho`, abaixo), e e
 * gravado um instante depois da ultima tecla. Ao abrir a tela ele volta com o
 * aviso de quando foi salvo e a opcao de descarta-lo; cadastrar o apaga.
 */
export function PaginaCadastro() {
  const base = useId();
  const navegar = useNavigate();
  const sessao = useSessao();
  const apoio = useCadastrosDeApoio(apoioLigado(sessao.data?.permissoes, 'equipamento.gerir'));
  const listaDeTipos = useTiposDeEquipamento(sessao.data?.permissoes.includes('equipamento.ler') ?? false);
  const cadastrar = useCadastrarEquipamento();

  const [descricao, setDescricao] = useState('');
  const [configuracaoId, setConfiguracaoId] = useState('');
  const [filialId, setFilialId] = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [motorModelo, setMotorModelo] = useState('');
  const [motorSerie, setMotorSerie] = useState('');
  const [ano, setAno] = useState('');
  const [medidor, setMedidor] = useState('');
  const [tipo, setTipo] = useState('');
  const [capacidade, setCapacidade] = useState('');
  const [leitura, setLeitura] = useState('');
  const [patrimonio, setPatrimonio] = useState('');
  const [codigoInterno, setCodigoInterno] = useState('');
  const [combustivel, setCombustivel] = useState('');
  const [abasteceEmPosto, setAbasteceEmPosto] = useState('');
  const [anoModelo, setAnoModelo] = useState('');
  const [anoAquisicao, setAnoAquisicao] = useState('');
  const [placa, setPlaca] = useState('');
  const [renavam, setRenavam] = useState('');
  /* Trocar a `rodada` remonta os campos: e o que apaga a mensagem de erro que
     cada campo guarda para si quando o rascunho e descartado. */
  const [rodada, setRodada] = useState(0);

  /* AS DUAS CAMADAS (31/10/2026, o desenho do lancamento de abastecimento):
     o que a configuracao responde aparece em etiqueta e abre em "Ajustar";
     o raro fica em "Mais dados". */
  const [ajustandoHeranca, setAjustandoHeranca] = useState(false);
  const [comMaisDados, setComMaisDados] = useState(false);
  /* O que a PESSOA tocou. Descricao e filial que ela nao tocou sao do
     sistema: a sugestao acompanha a configuracao e a filial unica. */
  const [descricaoTocada, setDescricaoTocada] = useState(false);
  const [filialTocada, setFilialTocada] = useState(false);
  const [erroDaDescricao, setErroDaDescricao] = useState<string | null>(null);

  const campos = useMemo<CamposDoCadastro>(
    () => ({
      descricao,
      configuracaoId,
      filialId,
      numeroSerie,
      motorModelo,
      motorSerie,
      ano,
      medidor,
      tipo,
      capacidade,
      leitura,
      patrimonio,
      codigoInterno,
      combustivel,
      abasteceEmPosto,
      anoModelo,
      anoAquisicao,
      placa,
      renavam,
    }),
    [
      descricao,
      configuracaoId,
      filialId,
      numeroSerie,
      motorModelo,
      motorSerie,
      ano,
      medidor,
      tipo,
      capacidade,
      leitura,
      patrimonio,
      codigoInterno,
      combustivel,
      abasteceEmPosto,
      anoModelo,
      anoAquisicao,
      placa,
      renavam,
    ],
  );
  /* O RASCUNHO leva so o que a pessoa escreveu: a descricao sugerida e a
     filial unica voltam sozinhas, e sem isto a tela recem-aberta ja contaria
     como rascunho. */
  const camposDoRascunho = useMemo<CamposDoCadastro>(
    () => ({ ...campos, descricao: descricaoTocada ? descricao : '', filialId: filialTocada ? filialId : '' }),
    [campos, descricao, descricaoTocada, filialId, filialTocada],
  );

  /* O que o rascunho traz de CADASTRO (configuracao, filial) so volta se ainda
     existir: um id apagado depois do rascunho seria recusado ao cadastrar, sem
     a pessoa ter como ver por que. */
  const configuracoes = apoio.data?.configuracoes;
  const filiais = apoio.data?.filiais;
  const tipos = listaDeTipos.data;
  const aplicar = useCallback(
    (c: CamposDoCadastro) => {
      setDescricao(c.descricao);
      setDescricaoTocada(c.descricao.trim() !== '');
      setConfiguracaoId((configuracoes ?? []).some((o) => o.id === c.configuracaoId) ? c.configuracaoId : '');
      const filialDoRascunho = (filiais ?? []).some((o) => o.id === c.filialId) ? c.filialId : '';
      setFilialId(filialDoRascunho);
      setFilialTocada(filialDoRascunho !== '');
      setNumeroSerie(c.numeroSerie);
      setMotorModelo(c.motorModelo);
      setMotorSerie(c.motorSerie);
      setAno(c.ano);
      setMedidor(c.medidor);
      /* O tipo do rascunho so volta se estiver na lista, escrito como ela escreve. */
      setTipo(tipoDaLista(tipos ?? [], c.tipo)?.nome ?? '');
      setCapacidade(c.capacidade);
      setLeitura(c.leitura);
      setPatrimonio(c.patrimonio);
      setCodigoInterno(c.codigoInterno);
      setCombustivel(COMBUSTIVEIS.some((o) => o.valor === c.combustivel) ? c.combustivel : '');
      setAbasteceEmPosto(c.abasteceEmPosto === 'sim' ? 'sim' : '');
      setAnoModelo(c.anoModelo);
      setAnoAquisicao(c.anoAquisicao);
      setPlaca(c.placa);
      setRenavam(c.renavam);
      /* O que voltou escrito nas camadas escondidas abre a camada: rascunho
         que volta para dentro de um botao fechado e rascunho que ninguem ve. */
      if ([c.medidor, c.capacidade, c.motorModelo].some((v) => v.trim() !== '')) setAjustandoHeranca(true);
      if (NOMES_DE_MAIS_DADOS.some((nome) => c[nome].trim() !== '')) setComMaisDados(true);
    },
    [configuracoes, filiais, tipos],
  );

  const rascunho = useRascunho(
    sessao.data ? `ct-frotas:rascunho:${sessao.data.empresa}:${sessao.data.usuarioId}:/equipamentos/novo` : null,
    /* So restaura depois de saber quais cadastros e quais tipos existem. */
    !apoio.isPending && !listaDeTipos.isLoading,
    camposDoRascunho,
    aplicar,
  );

  function descartarRascunho() {
    rascunho.descartar();
    aplicar(CAMPOS_VAZIOS);
    setAjustandoHeranca(false);
    setComMaisDados(false);
    setErroDaDescricao(null);
    setRodada((atual) => atual + 1);
  }

  const configuracao = (configuracoes ?? []).find((c) => c.id === configuracaoId) ?? null;

  /* A DESCRICAO SUGERIDA (`heranca-do-cadastro.ts`): tipo, fabricante e
     modelo, enquanto a pessoa nao escreveu a dela. */
  const sugestao = descricaoSugerida(tipo, configuracao);
  useEffect(() => {
    if (descricaoTocada) return;
    setDescricao(sugestao ?? '');
  }, [sugestao, descricaoTocada]);
  const descricaoPreenchida = !descricaoTocada && sugestao !== null && descricao === sugestao;

  /* A FILIAL UNICA: a empresa com uma filial so ja a tem escolhida. O campo
     continua opcional e continua lista; "Sem filial" e uma resposta. */
  const filialUnica = filiais?.length === 1 ? (filiais[0]?.id ?? null) : null;
  useEffect(() => {
    if (filialTocada || filialUnica === null) return;
    setFilialId(filialUnica);
  }, [filialUnica, filialTocada]);
  const filialPreenchida = !filialTocada && filialUnica !== null && filialId === filialUnica;

  const anoNumero = ano.trim() === '' ? null : interpretarNumero(ano);
  const capacidadeNumero = capacidade.trim() === '' ? null : interpretarNumero(capacidade);

  const anoValido = erroDoAno(ano) === null;
  const capacidadeValida = capacidadeNumero === null || capacidadeNumero > 0;
  const leituraNumero = leitura.trim() === '' ? null : interpretarNumero(leitura);
  const leituraValida = leitura.trim() === '' || (leituraNumero !== null && leituraNumero >= 0);
  /* Os seis da 0060: placa e Renavam ja saem normalizados; nulo e campo com erro. */
  const complementar = lerFichaComplementar(campos, ano);

  /* A PREVIA do codigo (0131): o que a maquina deste tipo recebe agora. */
  const proximo = useProximoCodigo(tipo, sessao.data?.permissoes.includes('equipamento.ler') ?? false);

  /* Escolher a configuracao escolhe o tipo do MODELO, quando ele esta na lista
     e a pessoa ainda nao escolheu outro. */
  function escolherConfiguracao(id: string) {
    const antes = tipoSugeridoDoModelo(tipos ?? [], configuracoes?.find((c) => c.id === configuracaoId)?.tipo);
    const depois = tipoSugeridoDoModelo(tipos ?? [], configuracoes?.find((c) => c.id === id)?.tipo);
    setConfiguracaoId(id);
    if (depois !== '' && (tipo === '' || tipo === antes)) setTipo(depois);
  }

  /* A camada escondida com erro abre sozinha: o botao desligado sem o porque
     a vista e o beco que esta tela nao pode ter. */
  const herancaComErro = !capacidadeValida;
  const maisDadosComErro =
    erroDoAnoModelo(anoModelo, ano) !== null || erroDoAnoAquisicao(anoAquisicao, ano) !== null || erroDoRenavam(renavam) !== null;
  const herancaAberta = ajustandoHeranca || herancaComErro;
  const maisDadosAbertos = comMaisDados || maisDadosComErro;

  const medidorQueVale = medidorEfetivo(medidor, configuracao);
  const herdadas = etiquetasHerdadas({ medidor, capacidade, motor: motorModelo }, configuracao);

  const podeGravar =
    tipo.trim().length > 0 &&
    descricao.trim().length > 0 &&
    anoValido &&
    capacidadeValida &&
    leituraValida &&
    complementar !== null &&
    !cadastrar.isPending;

  /* A tela INTEIRA é a ação: sem `equipamento.gerir`, o estado sem permissão
     com o perfil a pedir, e não um formulário que só o envio recusaria (prova
     do marco, 07/10/2026). */
  if (sessao.data && !sessao.data.permissoes.includes('equipamento.gerir')) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Frota"
            titulo="Cadastrar equipamento"
            acoes={
              <Link to="/equipamentos" search={{}} className="botao botao-secundario">
                Voltar à frota
              </Link>
            }
          />
          <SemPermissao modulo="o cadastro de equipamentos" perfil="Administrador" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Frota"
          titulo="Cadastrar equipamento"
          subtitulo="O tipo e a descrição bastam. O resto vem da configuração ou fica em Mais dados."
          acoes={
            <Link to="/equipamentos" search={{}} className="botao botao-secundario">
              Voltar à frota
            </Link>
          }
        />

        {/* O FORMULARIO E UM CARD (R01, p.28, o exemplo em L): fundo branco,
            e a barra de acoes no rodape dele, flutuando. Direto sobre o fundo
            da pagina a ajuda e o "opcional", em `--text-muted`, ficavam abaixo
            de 4,5:1. */}
        <form
          className={CARTAO_DE_FORMULARIO}
          onSubmit={(evento) => {
            evento.preventDefault();
            if (!podeGravar || !complementar) return;
            cadastrar.mutate(
              {
                /* Sem `codigo` (0131): o sistema o cria pelo tipo ao gravar. */
                descricao: descricao.trim(),
                configuracaoId: configuracaoId || null,
                filialId: filialId || null,
                numeroSerie: numeroSerie.trim() || null,
                motorModelo: motorModelo.trim() || null,
                motorNumeroSerie: motorSerie.trim() || null,
                anoFabricacao: anoNumero,
                tipoMedidor: medidor === 'horimetro' || medidor === 'odometro' ? medidor : null,
                tipo: tipo.trim(),
                capacidadeTanqueL: capacidadeNumero,
                leituraInicial: leituraNumero,
                abasteceEmPosto: abasteceEmPosto === 'sim',
                ...complementar,
              },
              {
                onSuccess: () => {
                  rascunho.concluir();
                  void navegar({ to: '/equipamentos', search: {} });
                },
              },
            );
          }}
        >
          {rascunho.restauradoEm ? (
            <div
              role="status"
              className="flex flex-wrap items-center justify-between gap-2 rounded-t-card border-b border-borda-sutil bg-rebaixada px-6 py-2"
            >
              <span className="txt-apoio text-tinta-secundaria">
                {`Rascunho de ${formatarDataHora(rascunho.restauradoEm)} restaurado. Ele fica salvo neste navegador até você cadastrar.`}
              </span>
              <Botao nivel="terciario" onClick={descartarRascunho}>
                Descartar rascunho
              </Botao>
            </div>
          ) : null}

          {cadastrar.error ? (
            /* A recusa do servidor no TOPO do formulário (p.23). */
            <div className="px-6 pt-6">
              <MensagemDeErro erro={cadastrar.error} />
            </div>
          ) : null}

          <div key={rodada} className="flex flex-col">
            <SecaoDoFormulario primeira>
              {/* ---- Linha 1: o que é e como se chama ---- */}

              {/* O TIPO, DA LISTA (0131, decisão 57): é dele que o código
                  nasce, então vem primeiro. Escolher a configuração já escolhe
                  o tipo do modelo quando ele está na lista. */}
              {listaDeTipos.isLoading ? (
                <Campo id={`${base}-tipo`} rotulo="Tipo">
                  <ControleCarregando oQue="Carregando os tipos de máquina" />
                </Campo>
              ) : (
                <CampoSelecao
                  rotulo="Tipo"
                  valor={tipo}
                  aoMudar={setTipo}
                  rotuloVazio="Escolha o tipo"
                  opcoes={opcoesDeTipo(tipos ?? [])}
                  {...(listaDeTipos.isError
                    ? { erro: 'Não foi possível carregar a lista de tipos. Recarregue a página para tentar de novo.' }
                    : { ajuda: 'Da lista da empresa. É dele que o código nasce.' })}
                />
              )}

              {/* O CÓDIGO, que ninguém digita (0131): a prévia de agora,
                  renovada a cada troca de tipo. */}
              <CampoDoCodigo
                valor={textoDoCodigoNovo(tipo, proximo.data)}
                ajuda="Criado pelo sistema pelo tipo e pela sequência."
              />

              {/* O SEGUNDO CÓDIGO (0125): o nome da máquina na planilha da
                  operação e na boca do encarregado. Em maiúsculas enquanto se
                  digita, como ele é gravado. */}
              <CampoTexto
                rotulo="Código interno"
                opcional
                valor={codigoInterno}
                aoMudar={(v) => {
                  setCodigoInterno(v.toUpperCase());
                }}
                maxLength={40}
                placeholder="CB-07"
                ajuda="Como a empresa chama a máquina no dia a dia."
                validar={erroDoCodigoInterno}
              />

              {/* ---- Linha 2: o modelo, e a descrição que ele sugere ---- */}

              {/* As opcoes ainda vem (p.32): o rotulo fica, e so o controle
                  vira a barra de 40 px. */}
              <HistoriaNaMesa codigo="CMP-EQP-04">
                {apoio.isPending ? (
                  <Campo id={`${base}-configuracao`} rotulo="Configuração" opcional>
                    <ControleCarregando oQue="Carregando as configurações" />
                  </Campo>
                ) : (
                  <CampoSelecao
                    rotulo="Configuração"
                    opcional
                    valor={configuracaoId}
                    aoMudar={escolherConfiguracao}
                    rotuloVazio="Sem configuração"
                    opcoes={(apoio.data?.configuracoes ?? []).map((c) => opcaoDaConfiguracao(c, c.rotulo))}
                    ajuda="O modelo da máquina. Dele vêm o tipo, a descrição, o medidor e o tanque."
                  />
                )}
              </HistoriaNaMesa>

              <div className="campo-largo min-w-0">
                <Campo
                  id={`${base}-descricao`}
                  rotulo="Descrição"
                  preenchido={descricaoPreenchida}
                  erro={erroDaDescricao}
                  {...(descricaoPreenchida ? { ajuda: 'Veio do tipo e da configuração. Complete se quiser, como "20 t" ou "cabine fechada".' } : {})}
                >
                  <input
                    id={`${base}-descricao`}
                    type="text"
                    autoComplete="off"
                    value={descricao}
                    placeholder="Escavadeira hidráulica 20 t"
                    className="controle w-full"
                    onChange={(evento) => {
                      setDescricao(evento.target.value);
                      setDescricaoTocada(true);
                      setErroDaDescricao(null);
                    }}
                    onBlur={(evento) => {
                      setErroDaDescricao(evento.target.value.trim() ? null : 'Escreva a descrição da máquina para poder cadastrar.');
                    }}
                    {...(erroDaDescricao ? { 'aria-invalid': true, 'aria-describedby': `${base}-descricao-erro` } : {})}
                  />
                </Campo>
              </div>

              {/* ---- Linha 3: o que só se lê na máquina ---- */}

              <CampoTexto
                rotulo="Placa"
                opcional
                valor={placa}
                aoMudar={(v) => {
                  setPlaca(v.toUpperCase());
                }}
                placeholder="ABC1D23"
                maxLength={10}
                ajuda="Só o que roda em via pública. ABC1234 ou ABC1D23."
                validar={erroDaPlaca}
              />

              <CampoTexto
                rotulo="Ano de fabricação"
                opcional
                valor={ano}
                aoMudar={setAno}
                placeholder="2019"
                validar={erroDoAno}
              />

              {/* A LEITURA INICIAL (melhoria de 24/09): a maquina chega a frota
                  com o medidor andado, e sem ela a primeira preventiva nao tem de
                  onde contar. O rotulo segue o medidor que VALE: o escolhido, ou
                  o da configuracao. */}
              <CampoTexto
                rotulo={rotuloDaLeituraInicial(medidorQueVale)}
                opcional
                valor={leitura}
                aoMudar={setLeitura}
                placeholder="4.832"
                ajuda={`O que o medidor marca hoje${medidorQueVale === 'odometro' ? ', em km' : medidorQueVale === 'horimetro' ? ', em horas' : ''}. Em branco, a primeira leitura entra com o primeiro apontamento.`}
                validar={(v) => {
                  if (v.trim() === '') return null;
                  const n = interpretarNumero(v);
                  return n !== null && n >= 0 ? null : 'Informe a leitura em números, como 4.832, ou deixe em branco.';
                }}
              />

              {/* ---- O que a configuração responde, em etiqueta ---- */}

              <LinhaDoPreenchido
                rotulo={configuracao ? 'Da configuração' : 'Medidor e tanque'}
                aberta={herancaAberta}
                aoAlternar={() => {
                  setAjustandoHeranca((aberto) => !aberto);
                }}
                rotuloDoBotao="medidor e tanque"
                idDoConteudo={`${base}-heranca`}
              >
                {herdadas.map((e) => (
                  <EtiquetaDoPreenchido key={e.rotulo} rotulo={e.rotulo} valor={e.valor} vazia={e.vazio} />
                ))}
              </LinhaDoPreenchido>

              {herancaAberta ? (
                /* A camada aberta em bloco rebaixado (`grade-do-painel`: a
                   grade que mede a propria largura, dentro da secao). */
                <div id={`${base}-heranca`} className="campo-linha grade-do-painel bloco-rebaixado p-4">
                  <CampoSelecao
                    rotulo="Medidor"
                    opcional
                    valor={medidor}
                    aoMudar={setMedidor}
                    rotuloVazio={configuracao?.tipoMedidor ? 'Igual à configuração' : 'Herda da configuração'}
                    opcoes={[
                      { valor: 'horimetro', rotulo: 'Horímetro' },
                      { valor: 'odometro', rotulo: 'Odômetro' },
                    ]}
                  />

                  <CampoTexto
                    rotulo="Capacidade do tanque"
                    opcional
                    valor={capacidade}
                    aoMudar={setCapacidade}
                    placeholder={
                      configuracao?.capacidadeTanqueL === null || configuracao?.capacidadeTanqueL === undefined
                        ? '380'
                        : formatarInteiro(configuracao.capacidadeTanqueL)
                    }
                    ajuda="Em litros. Em branco, a máquina herda a capacidade da configuração."
                    validar={(v) => {
                      if (v.trim() === '') return null;
                      const n = interpretarNumero(v);
                      return n !== null && n > 0 ? null : 'Informe a capacidade em litros, ou deixe em branco.';
                    }}
                  />

                  {/* O MOTOR: o modelo pode vir da configuração (deixe em branco
                      para herdar); o número de série, em Mais dados, é sempre
                      desta máquina. */}
                  <CampoTexto
                    rotulo="Motor"
                    opcional
                    valor={motorModelo}
                    aoMudar={setMotorModelo}
                    placeholder={configuracao?.motorModelo ?? 'Cummins QSB6.7'}
                    ajuda="Em branco, herda o motor da configuração. Preencha só quando esta máquina estiver com outro."
                  />
                </div>
              ) : null}

              {/* ---- O raro: atrás de Mais dados ---- */}

              <BotaoDoRaro
                aberto={maisDadosAbertos}
                aoAlternar={() => {
                  setComMaisDados((aberto) => !aberto);
                }}
                rotulo="Mais dados"
                rotuloAberto="Fechar mais dados"
                idDoConteudo={`${base}-mais-dados`}
              />

              {maisDadosAbertos ? (
                <div id={`${base}-mais-dados`} className="campo-linha grade-do-painel bloco-rebaixado p-4">
                  {apoio.isPending ? (
                    <Campo id={`${base}-filial`} rotulo="Filial" opcional>
                      <ControleCarregando oQue="Carregando as filiais" />
                    </Campo>
                  ) : (
                    <Campo
                      id={`${base}-filial`}
                      rotulo="Filial"
                      opcional
                      preenchido={filialPreenchida}
                      {...(filialPreenchida ? { ajuda: 'A única filial da empresa.' } : {})}
                    >
                      <ListaDeEscolha
                        id={`${base}-filial`}
                        rotulo="Filial"
                        valor={filialId}
                        aoMudar={(v) => {
                          setFilialId(v);
                          setFilialTocada(true);
                        }}
                        rotuloVazio="Sem filial"
                        opcoes={(apoio.data?.filiais ?? []).map((f) => ({ valor: f.id, rotulo: f.rotulo }))}
                      />
                    </Campo>
                  )}

                  <CampoTexto
                    rotulo="Patrimônio"
                    opcional
                    valor={patrimonio}
                    aoMudar={setPatrimonio}
                    maxLength={40}
                    ajuda="O número do controle patrimonial da empresa."
                  />

                  <CampoSelecao
                    rotulo="Combustível"
                    opcional
                    valor={combustivel}
                    aoMudar={setCombustivel}
                    rotuloVazio="Não informado"
                    opcoes={COMBUSTIVEIS}
                    ajuda="O posto já sugere este produto no abastecimento."
                  />

                  <CampoTexto
                    rotulo="Número de série"
                    opcional
                    valor={numeroSerie}
                    aoMudar={setNumeroSerie}
                    ajuda="Está na plaqueta de identificação da máquina. Não se repete dentro da empresa."
                  />

                  <CampoTexto
                    rotulo="Número de série do motor"
                    opcional
                    valor={motorSerie}
                    aoMudar={setMotorSerie}
                    ajuda="Está na plaqueta do motor. Não se repete dentro da empresa."
                  />

                  <CampoTexto
                    rotulo="Renavam"
                    opcional
                    valor={renavam}
                    aoMudar={setRenavam}
                    maxLength={14}
                    ajuda="Os 11 dígitos do documento. Com 9 ou 10, os zeros à esquerda se completam."
                    validar={erroDoRenavam}
                  />

                  <CampoTexto
                    rotulo="Ano do modelo"
                    opcional
                    valor={anoModelo}
                    aoMudar={setAnoModelo}
                    placeholder={ano.trim() === '' ? '2020' : ano.trim()}
                    ajuda="O de fabricação ou o seguinte."
                    validar={(v) => erroDoAnoModelo(v, ano)}
                  />

                  <CampoTexto
                    rotulo="Ano de aquisição"
                    opcional
                    valor={anoAquisicao}
                    aoMudar={setAnoAquisicao}
                    placeholder="2021"
                    validar={(v) => erroDoAnoAquisicao(v, ano)}
                  />

                  {/* 0133 (decisao 59): a maquina que abastece fora dos tanques
                      da empresa, e por isso entra na lista do posto parceiro. */}
                  <CaixaAbasteceEmPosto
                    id="cadastro-abastece-em-posto"
                    marcada={abasteceEmPosto === 'sim'}
                    aoMudar={(marcada) => {
                      setAbasteceEmPosto(marcada ? 'sim' : '');
                    }}
                  />
                </div>
              ) : null}
            </SecaoDoFormulario>
          </div>

          {/* Barra de acoes flutuando no rodape do card (R01, p.28): primario
              a direita e "Cancelar" terciario a esquerda dele (p.15). O
              primario e a unica acao laranja desta tela. Cancelar nao apaga o
              rascunho: ele volta na proxima vez, com a opcao de descartar. */}
          <div className={BARRA_DO_CARTAO}>
            <Link to="/equipamentos" search={{}} className="botao botao-terciario">
              Cancelar
            </Link>
            <Botao type="submit" nivel="primario" disabled={!podeGravar}>
              {cadastrar.isPending ? 'Cadastrando' : 'Cadastrar equipamento'}
            </Botao>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Os campos que moram em "Mais dados": o que volta escrito do rascunho abre a camada. */
const NOMES_DE_MAIS_DADOS = [
  'patrimonio',
  'combustivel',
  'numeroSerie',
  'motorSerie',
  'renavam',
  'anoModelo',
  'anoAquisicao',
  'abasteceEmPosto',
] as const;

/** Os dezoito campos do cadastro (o tipo no lugar do codigo, desde a 0131), como texto — do jeito que estao na tela. */
interface CamposDoCadastro {
  /* Sem `codigo` (0131): o rascunho antigo que o traga perde so ele. */
  readonly descricao: string;
  readonly configuracaoId: string;
  readonly filialId: string;
  readonly numeroSerie: string;
  readonly motorModelo: string;
  readonly motorSerie: string;
  readonly ano: string;
  readonly medidor: string;
  readonly tipo: string;
  readonly capacidade: string;
  readonly leitura: string;
  readonly patrimonio: string;
  readonly codigoInterno: string;
  readonly combustivel: string;
  /** 0133: `'sim'` ou `''`. */
  readonly abasteceEmPosto: string;
  readonly anoModelo: string;
  readonly anoAquisicao: string;
  readonly placa: string;
  readonly renavam: string;
}

const CAMPOS_VAZIOS: CamposDoCadastro = {
  descricao: '',
  configuracaoId: '',
  filialId: '',
  numeroSerie: '',
  motorModelo: '',
  motorSerie: '',
  ano: '',
  medidor: '',
  tipo: '',
  capacidade: '',
  leitura: '',
  patrimonio: '',
  codigoInterno: '',
  combustivel: '',
  abasteceEmPosto: '',
  anoModelo: '',
  anoAquisicao: '',
  placa: '',
  renavam: '',
};

const NOMES_DOS_CAMPOS = Object.keys(CAMPOS_VAZIOS) as readonly (keyof CamposDoCadastro)[];

/** Quanto esperar depois da ultima tecla para gravar: nao grava a cada letra. */
const ESPERA_PARA_GRAVAR_MS = 800;

/**
 * Rascunho automatico de formulario longo (p.16).
 *
 * Mora no `localStorage` DESTE navegador, sob uma chave por empresa, pessoa e
 * tela — e so. Nao vai ao servidor: e a copia de seguranca de quem esta
 * digitando, nao um cadastro pela metade que outra pessoa veria. Todo acesso
 * esta em `try/catch`, porque com o armazenamento bloqueado o navegador
 * LEVANTA em vez de devolver nulo (ver `lib/tema`); sem rascunho, a tela
 * funciona igual.
 *
 * `pronto` segura a restauracao ate a tela saber o que restaurar; `aplicar`
 * devolve os campos a tela. O rascunho vazio nao e gravado — e apaga o que
 * houver.
 */
function useRascunho(
  chave: string | null,
  pronto: boolean,
  campos: CamposDoCadastro,
  aplicar: (campos: CamposDoCadastro) => void,
) {
  const [restauradoEm, setRestauradoEm] = useState<Date | null>(null);
  /* A chave ja lida. Restaurar e UMA vez por abertura da tela: depois disso o
     que vale e o que a pessoa esta digitando. */
  const lida = useRef<string | null>(null);
  /* Depois de cadastrar, nenhuma gravacao atrasada pode ressuscitar o rascunho. */
  const concluido = useRef(false);

  useEffect(() => {
    if (!chave || !pronto || lida.current === chave) return;
    lida.current = chave;
    const salvo = lerRascunho(chave);
    if (!salvo) return;
    aplicar(salvo.campos);
    setRestauradoEm(salvo.salvoEm);
  }, [chave, pronto, aplicar]);

  useEffect(() => {
    if (!chave || lida.current !== chave || concluido.current) return;
    const relogio = window.setTimeout(() => {
      if (!concluido.current) gravarRascunho(chave, campos);
    }, ESPERA_PARA_GRAVAR_MS);
    return () => {
      window.clearTimeout(relogio);
    };
  }, [chave, campos]);

  return {
    restauradoEm,
    /** Apaga o rascunho guardado. Quem chama limpa os campos. */
    descartar() {
      setRestauradoEm(null);
      if (chave) apagarRascunho(chave);
    },
    /** O cadastro foi gravado: o rascunho sai e nao volta. */
    concluir() {
      concluido.current = true;
      if (chave) apagarRascunho(chave);
    },
  };
}

function lerRascunho(chave: string): { readonly salvoEm: Date; readonly campos: CamposDoCadastro } | null {
  let texto: string | null = null;
  try {
    texto = window.localStorage.getItem(chave);
  } catch {
    return null;
  }
  if (!texto) return null;

  let lido: unknown;
  try {
    lido = JSON.parse(texto);
  } catch {
    return null;
  }
  if (typeof lido !== 'object' || lido === null) return null;
  const { salvoEm, campos } = lido as { salvoEm?: unknown; campos?: unknown };
  if (typeof salvoEm !== 'string' || typeof campos !== 'object' || campos === null) return null;
  const quando = new Date(salvoEm);
  if (Number.isNaN(quando.getTime())) return null;

  const guardados = campos as Record<string, unknown>;
  const restaurados = Object.fromEntries(
    NOMES_DOS_CAMPOS.map((nome) => {
      const valor = guardados[nome];
      return [nome, typeof valor === 'string' ? valor : ''];
    }),
  ) as unknown as CamposDoCadastro;
  return NOMES_DOS_CAMPOS.some((nome) => restaurados[nome].trim() !== '')
    ? { salvoEm: quando, campos: restaurados }
    : null;
}

function gravarRascunho(chave: string, campos: CamposDoCadastro): void {
  if (NOMES_DOS_CAMPOS.every((nome) => campos[nome].trim() === '')) {
    apagarRascunho(chave);
    return;
  }
  try {
    window.localStorage.setItem(chave, JSON.stringify({ salvoEm: new Date().toISOString(), campos }));
  } catch {
    // Armazenamento bloqueado ou cheio: sem rascunho, e o formulario segue.
  }
}

function apagarRascunho(chave: string): void {
  try {
    window.localStorage.removeItem(chave);
  } catch {
    // Armazenamento bloqueado: nao havia o que apagar.
  }
}
